import { useState } from "react";
import { createInitiative, createIssue, uploadAttachment } from "@/lib/api";
import AttachmentField from "../issue/AttachmentField";
import {
  PRIORITIES,
  ISSUE_TYPES,
  ISSUE_TYPE_CONFIG,
  ENVIRONMENTS,
  ENV_COLORS,
} from "@/lib/constants";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

export default function CreateModal({ open, onOpenChange, projects, users, currentUser }) {
  const [issueType, setIssueType] = useState("BACKEND");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [projectId, setProjectId] = useState("");
  const [priority, setPriority] = useState("MEDIUM");
  const [assigneeId, setAssigneeId] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [releaseDate, setReleaseDate] = useState(null);
  const [environment, setEnvironment] = useState("");
  // Initiative child task checkboxes
  const [createDesign, setCreateDesign] = useState(true);
  const [createFrontend, setCreateFrontend] = useState(true);
  const [createBackend, setCreateBackend] = useState(true);
  const [createQA, setCreateQA] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Attachment state
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [attachmentError, setAttachmentError] = useState(null);
  const [attachmentReset, setAttachmentReset] = useState(false);

  const isInitiative = issueType === "INITIATIVE";
  const isBug = issueType === "BUG";

  const resetForm = () => {
    setIssueType("BACKEND");
    setTitle("");
    setDescription("");
    setProjectId("");
    setPriority("MEDIUM");
    setAssigneeId("");
    setDueDate(null);
    setReleaseDate(null);
    setEnvironment("");
    setCreateDesign(true);
    setCreateFrontend(true);
    setCreateBackend(true);
    setCreateQA(true);
    setAttachmentFile(null);
    setAttachmentReset((r) => !r);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !projectId) {
      toast.error("Please fill title and project");
      return;
    }
    if (isInitiative && !releaseDate) {
      toast.error("Release date is required for initiatives");
      return;
    }
    try {
      setSubmitting(true);
      let attachment_url = null;
      if (attachmentFile) {
        try {
          const result = await uploadAttachment(attachmentFile);
          attachment_url = result.attachment_url;
        } catch (uploadErr) {
          toast.error("Failed to upload image. Please try again.");
          return;
        }
      }
      if (isInitiative) {
        await createInitiative({
          project_id: projectId,
          title: title.trim(),
          description,
          priority,
          release_date: releaseDate ? format(releaseDate, "yyyy-MM-dd") : null,
          create_design: createDesign,
          create_frontend: createFrontend,
          create_backend: createBackend,
          create_qa: createQA,
          ...(attachment_url ? { attachment_url } : {}),
        });
        toast.success("Initiative created with linked tasks");
      } else {
        const payload = {
          project_id: projectId,
          type: issueType,
          title: title.trim(),
          description,
          priority,
          assignee_id: assigneeId || null,
          reporter_id: currentUser?.id || null,
          due_date: dueDate ? format(dueDate, "yyyy-MM-dd") : null,
          release_date: releaseDate ? format(releaseDate, "yyyy-MM-dd") : null,
        };
        if (isBug && environment) {
          payload.environment = environment;
        }
        if (attachment_url) {
          payload.attachment_url = attachment_url;
        }
        await createIssue(payload);
        toast.success(`${ISSUE_TYPE_CONFIG[issueType]?.label || "Issue"} created`);
      }
      resetForm();
      onOpenChange(false);
    } catch (e) {
      toast.error("Failed to create");
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const isValid = title.trim() && projectId && (!isInitiative || releaseDate);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px] rounded-sm p-0 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle
            className="text-xl font-bold"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="create-modal-title"
          >
            Create Issue
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Create a new issue, bug, or initiative.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 pt-4 space-y-4">
          {/* Issue Type */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
              Type *
            </label>
            <Select value={issueType} onValueChange={setIssueType}>
              <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="create-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-sm">
                {ISSUE_TYPES.map((t) => {
                  const cfg = ISSUE_TYPE_CONFIG[t] || {};
                  return (
                    <SelectItem key={t} value={t} className="rounded-sm text-sm">
                      <div className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: cfg.text }}
                        />
                        {cfg.label}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
              Title *
            </label>
            <input
              data-testid="create-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Issue title"
              className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
            />
          </div>

          {/* Description */}
          <div className="space-y-1.5">
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
              Description
            </label>
            <textarea
              data-testid="create-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the issue..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-sm text-sm resize-y focus:border-brand-rose focus:ring-1 focus:ring-brand-rose outline-none transition-all"
            />
          </div>

          {/* Attachment */}
          <AttachmentField
            onAttachmentChange={(file) => setAttachmentFile(file)}
            onError={(msg) => setAttachmentError(msg)}
            reset={attachmentReset}
          />

          {/* Project + Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Project *
              </label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="create-project-select">
                  <SelectValue placeholder="Select project" />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  {projects.map((p) => (
                    <SelectItem key={p.id} value={p.id} className="rounded-sm text-sm">
                      <span className="font-mono text-gray-400 mr-1">{p.key}</span>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Priority
              </label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="create-priority-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p} value={p} className="rounded-sm text-sm">
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Assignee (non-initiative only) */}
          {!isInitiative && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Assignee
              </label>
              <Select value={assigneeId || "none"} onValueChange={(v) => setAssigneeId(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="create-assignee-select">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value="none" className="rounded-sm text-sm">Unassigned</SelectItem>
                  {(users || []).map((u) => (
                    <SelectItem key={u.id} value={u.id} className="rounded-sm text-sm">
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-[9px] font-bold text-gray-600">
                          {u.full_name?.charAt(0)}
                        </span>
                        {u.full_name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Due Date + Release Date */}
          <div className="grid grid-cols-2 gap-4">
            {!isInitiative && (
              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                  Due Date
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      data-testid="create-due-date-picker"
                      className="flex items-center gap-2 w-full h-9 px-3 border border-gray-200 rounded-sm text-sm hover:border-brand-rose transition-colors text-left"
                    >
                      <CalendarDays className="w-4 h-4 text-gray-400" />
                      <span className={dueDate ? "text-black" : "text-gray-400"}>
                        {dueDate ? format(dueDate, "MMM d, yyyy") : "Set due date"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 rounded-sm" align="start">
                    <Calendar mode="single" selected={dueDate} onSelect={setDueDate} />
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <div className={`space-y-1.5 ${isInitiative ? "col-span-2" : ""}`}>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Release Date {isInitiative ? "*" : ""}
              </label>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    data-testid="create-release-date-picker"
                    className="flex items-center gap-2 w-full h-9 px-3 border border-gray-200 rounded-sm text-sm hover:border-brand-rose transition-colors text-left"
                  >
                    <CalendarDays className="w-4 h-4 text-gray-400" />
                    <span className={releaseDate ? "text-black" : "text-gray-400"}>
                      {releaseDate ? format(releaseDate, "MMM d, yyyy") : "Set release date"}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 rounded-sm" align="start">
                  <Calendar mode="single" selected={releaseDate} onSelect={setReleaseDate} />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Bug Environment */}
          {isBug && (
            <div className="space-y-1.5">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Environment
              </label>
              <Select value={environment || "none"} onValueChange={(v) => setEnvironment(v === "none" ? "" : v)}>
                <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="create-environment-select">
                  <SelectValue placeholder="Select environment" />
                </SelectTrigger>
                <SelectContent className="rounded-sm">
                  <SelectItem value="none" className="rounded-sm text-sm">Not Set</SelectItem>
                  {ENVIRONMENTS.map((env) => {
                    const c = ENV_COLORS[env] || {};
                    return (
                      <SelectItem key={env} value={env} className="rounded-sm text-sm">
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.text }} />
                          {env}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Initiative child task checkboxes */}
          {isInitiative && (
            <div className="space-y-2">
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-500">
                Auto-create linked tasks
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { label: "Design", checked: createDesign, set: setCreateDesign, testId: "check-design" },
                  { label: "Frontend", checked: createFrontend, set: setCreateFrontend, testId: "check-frontend" },
                  { label: "Backend", checked: createBackend, set: setCreateBackend, testId: "check-backend" },
                  { label: "QA", checked: createQA, set: setCreateQA, testId: "check-qa" },
                ].map((item) => (
                  <label
                    key={item.label}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-sm hover:border-gray-400 cursor-pointer transition-colors"
                  >
                    <Checkbox
                      data-testid={item.testId}
                      checked={item.checked}
                      onCheckedChange={item.set}
                      className="rounded-sm"
                    />
                    <span className="text-sm font-medium text-gray-700">{item.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              data-testid="create-cancel-btn"
              onClick={() => { resetForm(); onOpenChange(false); }}
              className="bg-white text-brand border border-gray-300 hover:border-brand-rose rounded-sm px-4 py-2 text-sm font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              data-testid="create-submit-btn"
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              className="bg-brand text-white hover:bg-brand-dark rounded-sm px-4 py-2 text-sm font-semibold transition-colors disabled:opacity-40"
            >
              {submitting ? "Creating..." : isInitiative ? "Create Initiative" : "Create Issue"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
